/**
 * Shared indicator library — the single source of truth for indicator MATH
 * and INPUTS, reused by:
 *   - the scanner worker (computes the scalar at fixed preset params → scanner_panel)
 *   - the price chart (computes the full series at user-chosen params → plotted)
 *   - the watchlist grid (reads the scanner's preset scalars)
 *
 * Each indicator computes an INDEX-ALIGNED series on plain OHLCV arrays
 * (`computeSeries`), so the same function serves both the chart (whole series)
 * and the scanner/watchlist (last value via `lastFinite`). Chart `Bar` and
 * scanner `StoredBar` differ only in field names — callers pass arrays.
 */

/** Column-major OHLCV. Only the fields an indicator needs are required.
 *  `times`      — per-bar timestamps (ms), index-aligned to `closes`. Needed by
 *                 weekly/HTF indicators that bucket daily bars into ISO weeks.
 *  `compareCloses`/`compareTimes` — a benchmark/comparative series (e.g. SPY),
 *                 supplied to relative-strength indicators (`needsComparative`).
 *  All optional — an indicator reads only what it needs; absent → it returns
 *  nulls rather than throwing. */
export type OHLCV = {
  closes: number[];
  volumes: number[];
  opens?: number[];
  highs?: number[];
  lows?: number[];
  times?: number[];
  compareCloses?: number[];
  compareTimes?: number[];
};

/** One configurable input. Numeric or a small enumerated choice (e.g. a mode).
 *  `default` is numeric for both — select choices carry the number directly. */
export type SharedParam =
  | {
      id: string;
      label: string;
      kind: "number";
      default: number;
      min?: number;
      max?: number;
      step?: number;
    }
  | {
      id: string;
      label: string;
      kind: "select";
      default: number;
      choices: ReadonlyArray<{ value: number; label: string }>;
    };

/** Resolved params for a compute call: `{ paramId: numericValue }`. */
export type IndicatorParams = Record<string, number>;

/** How a scalar value (last finite of the series) should be displayed when the
 *  indicator is added as a scanner/watchlist COLUMN. The table maps these onto
 *  its own `ColKind`:
 *    pct      signed percent, heat-eligible  (e.g. %vs SMA, ROC)
 *    percent  unsigned percent               (e.g. RVol, ATR%)
 *    num      plain number, 1-2 dp           (e.g. RSI, MFI, ADX)
 *    int      plain integer, thousands-sep   (e.g. bar count)
 *    compact  large count, K/M/B abbreviated (e.g. Volume → "1.2M")
 *    ratio    x-multiple, 2 dp               (e.g. Bollinger %B)
 *    signed   green ≥0 / red <0, signed      (e.g. fM, MACD hist, CCI, vol-z)
 *    state    a discrete regime/signal encoded as +1 / -1 / 0, rendered as
 *             text via `stateLabels` (e.g. "Buy"/"Sell"/"—"), sign-tinted
 *    age      bars since the current state began / since the last fire —
 *             an unsigned integer count (e.g. "7")
 *    quad     a 4-state classification encoded as 1 / 2 / 3 / 4, each rendered
 *             as a distinct filled color (e.g. PMC: bull-accel / bull-fade /
 *             bear-recover / bear-accel); 1-2 labelled pos, 3-4 neg via stateLabels
 */
export type SharedFormat =
  | "pct"
  | "percent"
  | "num"
  | "int"
  | "compact"
  | "ratio"
  | "signed"
  | "agesign" // signed days above (+) / below (−) a MA — green/red by sign
  | "state"
  | "age"
  | "quad";

/** What KIND of series `computeSeries` produces — lets the chart adapter pick
 *  the right track type and the column renderer pick the right formatter.
 *    continuous  a real-valued oscillator (default; e.g. RSI, fM, H/L Jump)
 *    state       +1 / -1 / 0 / null regime (e.g. Bongo, EMS, RSMA)
 *    fire        1 on a fired bar else 0 / null (e.g. Squeeze Release, Dots)
 *    age         bars-since companion of a state/fire series */
export type SharedIndicatorKind = "continuous" | "state" | "fire" | "age";

/** Text labels for a `state` indicator's +1 / -1 / 0 values (drives the chart
 *  legend and the scanner column text). Defaults to Buy / Sell / —. */
export type StateLabels = { pos: string; neg: string; zero?: string };

export type SharedIndicatorDef = {
  /** Stable id (e.g. "fm", "rvol", "rsi"). */
  id: string;
  /** Human label (e.g. "Force Momentum"). */
  label: string;
  /** Picker grouping (e.g. "Momentum", "Trend", "Volatility", "Volume"). */
  group?: string;
  params: ReadonlyArray<SharedParam>;
  /** Horizontal guide line(s) for the chart oscillator (e.g. [0] or [100]). */
  referenceLevels?: ReadonlyArray<number>;
  /** Color the plotted value green (≥0) / red (<0) by sign. */
  signColored?: boolean;
  /** Column display format hint (see SharedFormat). Defaults to "num". */
  format?: SharedFormat;
  /** Whether the column value is heat-color eligible (red↔green ramp). */
  heat?: boolean;
  /** Series kind — defaults to "continuous" so existing defs are unaffected. */
  kind?: SharedIndicatorKind;
  /** For `state` indicators: labels for +1 / -1 / 0 (default Buy / Sell / —). */
  stateLabels?: StateLabels;
  /** Name of the precomputed `scanner_panel`/payload field that holds this
   *  indicator's value AT DEFAULT PARAMS. When set, a scanner column at default
   *  params reads that field straight from the payload (instant, no fetch); any
   *  changed param falls back to an on-demand `computeSeries` recompute. The
   *  precomputed default MUST equal `lastFinite(computeSeries(bars, defaults))`. */
  panelKey?: string;
  /** For `state`/`fire` scanner cells: override the filled-cell background per
   *  side (hex). Lets e.g. Squeeze render a yellow badge, Release a blue one,
   *  LCD red. When unset, +1 fills green / -1 fills red. */
  cellColors?: { pos?: string; neg?: string };
  /** True when the indicator needs a comparative series (`compareCloses`),
   *  e.g. relative strength vs SPY — tells the scanner to load the benchmark. */
  needsComparative?: boolean;
  /** Skip this indicator in the scanner's per-tick LIVE overlay (Tier 2) and
   *  let it ride the ~60s batch cadence instead. Set on normalized oscillators
   *  whose per-trade value is too jumpy to track the chart (e.g. H/L Jump).
   *  State / quad / age / count columns are dropped from the overlay by kind
   *  automatically, so they don't need this flag. */
  noLiveOverlay?: boolean;
  /**
   * Opt a state / age / quad column INTO the scanner live overlay anyway.
   * Latched regimes that the chart paints from the forming bar (Bongo) must
   * recompute from the live close or Last ticks while Bongo stays on yesterday.
   */
  liveOverlay?: boolean;
  /** Cross-sectional indicator (e.g. IBD RS Rating) — its value is a rank of
   *  the symbol against the WHOLE universe, so it can't be computed from one
   *  symbol's bars. `computeSeries` is a no-op; the scanner indicator endpoint
   *  special-cases it and computes the rank over the full universe instead.
   *  Scanner-only (the chart catalog doesn't read shared defs). */
  crossSectional?: boolean;
  /**
   * Index-aligned output series, same length as `data.closes`. Entries are
   * `null` during warmup / when undefined. The chart plots the whole series;
   * the scanner/watchlist take the last finite value.
   */
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[];
};

/** Resolve a params object from a def's defaults, applying overrides. */
export function resolveParams(
  def: SharedIndicatorDef,
  overrides?: IndicatorParams,
): IndicatorParams {
  const out: IndicatorParams = {};
  for (const p of def.params) out[p.id] = p.default;
  if (overrides) {
    for (const p of def.params) {
      const v = overrides[p.id];
      if (typeof v === "number" && Number.isFinite(v)) out[p.id] = v;
    }
  }
  return out;
}
